import React, { createContext, useState, useContext, useEffect } from "react";
import {
  boosterData,
  getCreatorArchivedBoosters,
  getCreatorBoosters,
} from "../../services/BoosterServices";
import { useAuth } from "../AuthProvider";
import {
  UsePaginatedDataResult,
  usePaginatedData,
} from "../helper/usePaginatedData";

interface BoosterContextProps {
  boostersState: UsePaginatedDataResult<boosterData>;
  filteredBoosters: boosterData[];
  setFilteredBoosters: React.Dispatch<React.SetStateAction<boosterData[]>>;
  archivedBoostersState: UsePaginatedDataResult<boosterData>;
}

const CreatorBoosterContext = createContext<BoosterContextProps | undefined>(
  undefined
);

export const CreatorBoosterProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const boostersState = usePaginatedData<boosterData>();
  const archivedBoostersState = usePaginatedData<boosterData>();
  const [filteredBoosters, setFilteredBoosters] = useState<boosterData[]>([]);
  const { user } = useAuth();
  const limit = 8;

  useEffect(() => {
    const fetchBoosters = async () => {
      if (boostersState.loading || !boostersState.hasMore) return;

      boostersState.setLoading(true);
      boostersState.setError(null);
      try {
        const offset = (boostersState.page - 1) * limit;
        const fetchedBoosters = await getCreatorBoosters(limit, offset);

        if (fetchedBoosters.length < limit) {
          boostersState.setHasMore(false);
        }

        boostersState.setData((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });

        setFilteredBoosters((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });
      } catch (err) {
        boostersState.setError("Failed to fetch boosters");
      } finally {
        boostersState.setLoading(false);
      }
    };

    if (user && boostersState.page > 0) fetchBoosters();

    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boostersState.page]);

  useEffect(() => {
    if (user && archivedBoostersState.page > 0)
      archivedBoostersState.fetchData(limit, getCreatorArchivedBoosters);

    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedBoostersState.page]);

  useEffect(() => {
    boostersState.setPage(0);
    boostersState.setHasMore(true);
    setFilteredBoosters([]);
    boostersState.setData([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <CreatorBoosterContext.Provider
      value={{
        boostersState,
        filteredBoosters,
        setFilteredBoosters,
        archivedBoostersState,
      }}
    >
      {children}
    </CreatorBoosterContext.Provider>
  );
};

export const useCreatorBoostersContext = () => {
  const context = useContext(CreatorBoosterContext);
  if (!context) {
    throw new Error(
      "useBoosterContext must be used within a CreatorBoosterProvider"
    );
  }
  return context;
};
